"use strict";
/**
 * @license
 * Copyright 2018 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { WatchableValue } from "#src/trackable_value.js";
import { float32ToString } from "#src/util/float32_to_string.js";
import { vec3, vec4 } from "#src/util/geom.js";
import { hexEncodeByte } from "#src/util/hex.js";
export function parseColorSerialization(x) {
  const rgbaPattern = /^rgba\(([0-9]+), ([0-9]+), ([0-9]+), (0(?:\.[0-9]+)?)\)$/;
  {
    const m = x.match(rgbaPattern);
    if (m !== null) {
      return [
        parseInt(m[1], 10),
        parseInt(m[2], 10),
        parseInt(m[3], 10),
        parseFloat(m[4])
      ];
    }
  }
  const hexPattern = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/;
  {
    const m = x.match(hexPattern);
    if (m !== null) {
      return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16), 1];
    }
  }
  throw new Error(`Invalid serialized color: ${JSON.stringify(x)}.`);
}
export function parseRGBAColorSpecification(x) {
  try {
    if (typeof x !== "string") {
      throw new Error(`Expected string, but received ${JSON.stringify(x)}.`);
    }
    const context = document.createElement("canvas").getContext("2d");
    context.fillStyle = x;
    const result = parseColorSerialization(context.fillStyle);
    return vec4.fromValues(
      result[0] / 255,
      result[1] / 255,
      result[2] / 255,
      result[3]
    );
  } catch (parseError) {
    throw new Error(
      `Failed to parse color specification: ${parseError.message}`
    );
  }
}
export function parseRGBColorSpecification(x) {
  const result = parseRGBAColorSpecification(x);
  return result.subarray(0, 3);
}
export function packColor(x) {
  const size = x[3] === void 0 ? 3 : 4;
  let result = 0;
  for (let i = 0; i < size; i++) {
    result = (result << 8 >>> 0) + Math.min(255, Math.max(0, Math.round(x[size - 1 - i] * 255)));
  }
  return result;
}
export function unpackRGB(value) {
  return vec3.fromValues(
    (value >>> 0 & 255) / 255,
    (value >>> 8 & 255) / 255,
    (value >>> 16 & 255) / 255
  );
}
export function unpackRGBA(value) {
  return vec4.fromValues(
    (value >>> 0 & 255) / 255,
    (value >>> 8 & 255) / 255,
    (value >>> 16 & 255) / 255,
    (value >>> 24 & 255) / 255
  );
}
export function serializeColor(x) {
  if (x[3] === void 0 || x[3] === 1) {
    let result2 = "#";
    for (let i = 0; i < 3; ++i) {
      result2 += hexEncodeByte(
        Math.min(255, Math.max(0, Math.round(x[i] * 255)))
      );
    }
    return result2;
  }
  let result = "rgba(";
  for (let i = 0; i < 3; ++i) {
    if (i !== 0) {
      result += ", ";
    }
    result += Math.min(255, Math.max(0, Math.round(x[i] * 255)));
  }
  result += `, ${float32ToString(x[3])})`;
  return result;
}
export function srgbGammaExpand(value) {
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}
export function getRelativeLuminance(color) {
  const [r, g, b] = color;
  return 0.2126 * srgbGammaExpand(r) + 0.7152 * srgbGammaExpand(g) + 0.0722 * srgbGammaExpand(b);
}
export function useWhiteBackground(foregroundColor) {
  return getRelativeLuminance(foregroundColor) <= 0.179;
}
export class TrackableRGB extends WatchableValue {
  constructor(defaultValue) {
    super(vec3.clone(defaultValue));
    this.defaultValue = defaultValue;
  }
  toString() {
    return serializeColor(this.value);
  }
  toJSON() {
    if (vec3.equals(this.value, this.defaultValue)) {
      return void 0;
    }
    return serializeColor(this.value);
  }
  reset() {
    this.value = vec3.clone(this.defaultValue);
  }
  restoreState(x) {
    if (x === void 0) {
      this.reset();
      return;
    }
    const { value } = this;
    const newValue = parseRGBColorSpecification(x);
    if (!vec3.equals(value, newValue)) {
      this.value = newValue;
    }
  }
}
export class TrackableOptionalRGB extends WatchableValue {
  constructor() {
    super(void 0);
  }
  toJSON() {
    const { value } = this;
    if (value === void 0) return void 0;
    return serializeColor(value);
  }
  reset() {
    this.value = void 0;
  }
  restoreState(x) {
    if (x === void 0) {
      this.reset();
      return;
    }
    const { value } = this;
    const newValue = parseRGBColorSpecification(x);
    if (value === void 0 || !vec3.equals(value, newValue)) {
      this.value = newValue;
    }
  }
}
